import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Table,
  Tag,
  Space,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Switch,
  Statistic,
  Popconfirm,
  Empty,
  Spin,
  message,
  theme,
  Alert,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { authenticatedFetch } from "@/libs/api";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { useToken } = theme;

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

/**
 * Global Partner Revenue Shares management page.
 *
 * Super Admin only — manages every (view_group, company, share_percent) row
 * across the platform. Per-company snapshots show a read-only mirror of
 * these rows; this is the single source of truth.
 */
export default function VgShareManagement() {
  const { token } = useToken();

  // --- access control ---
  const roleType = (localStorage.getItem("roleType") || "").toUpperCase();
  const isSuperadmin = roleType === "SUPERADMIN";

  // --- data state ---
  const [shares, setShares] = useState([]);
  const [vgs, setVgs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);

  // --- filters ---
  const [filterVg, setFilterVg] = useState(null);
  const [filterCompany, setFilterCompany] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(true);

  // --- modal state ---
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [previewSum, setPreviewSum] = useState(null);
  const [form] = Form.useForm();

  // ----- API helpers -----
  const apiCall = useCallback(async (path, options = {}) => {
    const res = await authenticatedFetch(`${API_BASE}${path}`, options);
    const data = await res.json();
    if (!res.ok || data?.success === false) {
      const msg = data?.message || `HTTP ${res.status}`;
      throw new Error(msg);
    }
    return data;
  }, []);

  const fetchShares = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterVg) params.append("view_group_id", filterVg);
      if (filterCompany) params.append("company_id", filterCompany);
      if (includeInactive) params.append("include_inactive", "1");
      const data = await apiCall(`/api/vg-shares?${params.toString()}`);
      setShares(data.data || []);
    } catch (e) {
      message.error(`Load shares failed: ${e.message}`);
      setShares([]);
    } finally {
      setLoading(false);
    }
  }, [apiCall, filterVg, filterCompany, includeInactive]);

  const fetchReferenceData = useCallback(async () => {
    try {
      // VGs: all visible to current user. Superadmin should see all.
      const userId = localStorage.getItem("userId");
      const vgRes = await apiCall(`/api/groups?userId=${userId}`);
      const vgList = (vgRes.data || vgRes.viewGroups || []).map((vg) => ({
        id: vg.id,
        name: vg.name,
        companyId: vg.company_id,
      }));
      setVgs(vgList);

      const coRes = await apiCall(`/api/companies?pageSize=1000`);
      const coList = (coRes.data || []).map((c) => ({
        id: c.id,
        name: c.company_name || c.name,
      }));
      setCompanies(coList);
    } catch (e) {
      message.error(`Load VGs/companies failed: ${e.message}`);
    }
  }, [apiCall]);

  useEffect(() => {
    if (isSuperadmin) {
      fetchReferenceData();
    }
  }, [isSuperadmin, fetchReferenceData]);

  useEffect(() => {
    if (isSuperadmin) {
      fetchShares();
    }
  }, [isSuperadmin, fetchShares]);

  // ----- modal handlers -----
  const handleAdd = () => {
    setEditing(null);
    setPreviewSum(null);
    form.resetFields();
    form.setFieldsValue({
      effective_from: dayjs(),
      is_active: true,
      priority: 100,
    });
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditing(record);
    setPreviewSum(null);
    form.setFieldsValue({
      view_group_id: record.view_group_id,
      company_id: record.company_id,
      share_percent: Number(record.share_percent),
      effective_from: record.effective_from ? dayjs(record.effective_from) : null,
      effective_to: record.effective_to ? dayjs(record.effective_to) : null,
      description: record.description || "",
      priority: record.priority ?? 100,
      is_active: Number(record.is_active) === 1,
    });
    setModalOpen(true);
  };

  const handleDelete = async (record) => {
    try {
      await apiCall(`/api/vg-shares/${record.id}`, { method: "DELETE" });
      message.success(`Deleted share for ${record.company_id} ← ${record.view_group_id}`);
      fetchShares();
    } catch (e) {
      message.error(`Delete failed: ${e.message}`);
    }
  };

  const refreshPreviewSum = async (vgId, effectiveFrom) => {
    if (!vgId || !effectiveFrom) {
      setPreviewSum(null);
      return;
    }
    try {
      const fromStr = dayjs(effectiveFrom).format("YYYY-MM-DD");
      const data = await apiCall(
        `/api/vg-shares/preview-sum?view_group_id=${vgId}&effective_from=${fromStr}`
      );
      const baseSum = Number(data.existingSum) || 0;
      // If editing, subtract own current contribution
      const adjusted = editing
        ? Math.max(0, baseSum - Number(editing.share_percent || 0))
        : baseSum;
      setPreviewSum(adjusted);
    } catch (e) {
      setPreviewSum(null);
    }
  };

  const handleSave = async (values) => {
    const body = {
      view_group_id: values.view_group_id,
      company_id: values.company_id,
      share_percent: Number(values.share_percent),
      effective_from: dayjs(values.effective_from).format("YYYY-MM-DD"),
      effective_to: values.effective_to ? dayjs(values.effective_to).format("YYYY-MM-DD") : null,
      description: values.description || null,
      priority: values.priority ?? 100,
      is_active: values.is_active ? 1 : 0,
    };
    try {
      const url = editing ? `/api/vg-shares/${editing.id}` : `/api/vg-shares`;
      const method = editing ? "PUT" : "POST";
      await apiCall(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      message.success(editing ? "Updated" : "Created");
      setModalOpen(false);
      fetchShares();
    } catch (e) {
      // Backend returns rich error info on 409. Try to surface the most useful bit.
      message.error(`Save failed: ${e.message}`, 8);
    }
  };

  // ----- derived state -----
  const summary = useMemo(() => {
    const active = shares.filter((s) => Number(s.is_active) === 1);
    return {
      total: shares.length,
      active: active.length,
      uniqueVGs: new Set(active.map((s) => s.view_group_id)).size,
      uniqueCompanies: new Set(active.map((s) => s.company_id)).size,
    };
  }, [shares]);

  // ----- gate non-superadmins out -----
  if (!isSuperadmin) {
    return (
      <Card style={{ margin: 16 }}>
        <Alert
          type="warning"
          showIcon
          message="Super Admin only"
          description="Partner Revenue Shares management is restricted to Super Admin. Contact your administrator if you need access."
        />
      </Card>
    );
  }

  return (
    <div style={{ padding: 24 }}>
        <Title level={3} style={{ marginBottom: 8 }}>
          <DollarOutlined /> Partner Revenue Shares
        </Title>
        <Text type="secondary">
          Central configuration of (view group → partner company, %) splits used by settlement step 4.
          Per-company snapshots display read-only views of these rows.
        </Text>

        {/* Summary */}
        <Row gutter={16} style={{ marginTop: 24, marginBottom: 16 }}>
          <Col span={6}><Card><Statistic title="Total Shares" value={summary.total} /></Card></Col>
          <Col span={6}><Card><Statistic title="Active" value={summary.active} valueStyle={{ color: token.colorSuccess }} /></Card></Col>
          <Col span={6}><Card><Statistic title="VGs Involved" value={summary.uniqueVGs} /></Card></Col>
          <Col span={6}><Card><Statistic title="Partner Companies" value={summary.uniqueCompanies} /></Card></Col>
        </Row>

        {/* Toolbar */}
        <Card style={{ marginBottom: 16 }}>
          <Space wrap>
            <Select
              showSearch
              allowClear
              placeholder="Filter by View Group"
              style={{ width: 280 }}
              value={filterVg}
              onChange={setFilterVg}
              optionFilterProp="label"
              options={vgs.map((vg) => ({ value: vg.id, label: vg.name }))}
            />
            <Select
              showSearch
              allowClear
              placeholder="Filter by Partner Company"
              style={{ width: 280 }}
              value={filterCompany}
              onChange={setFilterCompany}
              optionFilterProp="label"
              options={companies.map((c) => ({ value: c.id, label: c.name }))}
            />
            <Switch
              checked={includeInactive}
              onChange={setIncludeInactive}
              checkedChildren="incl. inactive"
              unCheckedChildren="active only"
            />
            <Button icon={<ReloadOutlined />} onClick={fetchShares}>Refresh</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Add Share
            </Button>
          </Space>
        </Card>

        {/* Table */}
        <Card>
          {loading ? (
            <div style={{ textAlign: "center", padding: 48 }}><Spin /></div>
          ) : shares.length === 0 ? (
            <Empty description="No revenue shares" />
          ) : (
            <Table
              dataSource={shares}
              rowKey="id"
              pagination={{ pageSize: 25, showSizeChanger: true }}
              columns={[
                {
                  title: "View Group",
                  key: "vg",
                  render: (_, r) => (
                    <div>
                      <Text strong>{r.view_group_name || vgs.find((v) => v.id === r.view_group_id)?.name || r.view_group_id}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 11 }}>{r.view_group_id}</Text>
                    </div>
                  ),
                  sorter: (a, b) => String(a.view_group_id).localeCompare(String(b.view_group_id)),
                },
                {
                  title: "Partner Company",
                  key: "company",
                  render: (_, r) => (
                    <div>
                      <Text strong>{r.company_name || companies.find((c) => c.id === r.company_id)?.name || r.company_id}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 11 }}>{r.company_id}</Text>
                    </div>
                  ),
                  sorter: (a, b) => String(a.company_id).localeCompare(String(b.company_id)),
                },
                {
                  title: "Share %",
                  dataIndex: "share_percent",
                  key: "share_percent",
                  align: "right",
                  width: 100,
                  render: (v) => (
                    <Text strong style={{ color: token.colorPrimary }}>
                      {Number(v).toFixed(2)}%
                    </Text>
                  ),
                  sorter: (a, b) => Number(a.share_percent) - Number(b.share_percent),
                },
                {
                  title: "Description",
                  dataIndex: "description",
                  key: "description",
                  ellipsis: true,
                  render: (v) => v ? <Text>{v}</Text> : <Text type="secondary">—</Text>,
                },
                {
                  title: "Effective Period",
                  key: "period",
                  render: (_, r) => (
                    <span>
                      {dayjs(r.effective_from).format("YYYY-MM-DD")}
                      {" → "}
                      {r.effective_to ? dayjs(r.effective_to).format("YYYY-MM-DD") : "Indefinite"}
                    </span>
                  ),
                },
                {
                  title: "Status",
                  key: "status",
                  width: 90,
                  align: "center",
                  render: (_, r) => Number(r.is_active) === 1
                    ? <Tag color="success">Active</Tag>
                    : <Tag>Inactive</Tag>,
                  filters: [
                    { text: "Active", value: 1 },
                    { text: "Inactive", value: 0 },
                  ],
                  onFilter: (value, record) => Number(record.is_active) === value,
                },
                {
                  title: "Actions",
                  key: "actions",
                  width: 110,
                  align: "center",
                  render: (_, r) => (
                    <Space>
                      <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(r)} />
                      <Popconfirm
                        title="Delete this share?"
                        description="The row will be soft-deleted and set to inactive."
                        onConfirm={() => handleDelete(r)}
                        okText="Delete"
                        okButtonProps={{ danger: true }}
                      >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]}
            />
          )}
        </Card>

        {/* Add / Edit Modal */}
        <Modal
          title={editing ? "Edit Revenue Share" : "Add Revenue Share"}
          open={modalOpen}
          onCancel={() => setModalOpen(false)}
          onOk={() => form.submit()}
          okText={editing ? "Save" : "Create"}
          destroyOnClose
          width={680}
        >
          <Form form={form} layout="vertical" onFinish={handleSave}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="view_group_id"
                  label="View Group"
                  rules={[{ required: true, message: "Pick a view group" }]}
                >
                  <Select
                    showSearch
                    placeholder="Select view group"
                    disabled={!!editing}
                    optionFilterProp="label"
                    options={vgs.map((vg) => ({ value: vg.id, label: `${vg.name} (${vg.id})` }))}
                    onChange={(vgId) => {
                      const ef = form.getFieldValue("effective_from");
                      refreshPreviewSum(vgId, ef);
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="company_id"
                  label="Partner Company"
                  rules={[{ required: true, message: "Pick a partner company" }]}
                >
                  <Select
                    showSearch
                    placeholder="Select company"
                    disabled={!!editing}
                    optionFilterProp="label"
                    options={companies.map((c) => ({ value: c.id, label: `${c.name} (${c.id})` }))}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="share_percent"
                  label="Share %"
                  rules={[
                    { required: true, message: "Share % required" },
                    { type: "number", min: 0, max: 100, message: "0-100" },
                  ]}
                >
                  <InputNumber min={0} max={100} step={0.01} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="effective_from"
                  label="Effective From"
                  rules={[{ required: true, message: "Effective from required" }]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    onChange={(d) => {
                      const vgId = form.getFieldValue("view_group_id");
                      refreshPreviewSum(vgId, d);
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="effective_to" label="Effective To (optional)">
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="description" label="Description">
              <Input.TextArea rows={2} placeholder="e.g. 'Founding council share for Marion'" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="priority" label="Priority (lower = higher priority)">
                  <InputNumber min={0} max={9999} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="is_active" label="Active" valuePropName="checked">
                  <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                </Form.Item>
              </Col>
            </Row>

            {previewSum !== null && (
              <Alert
                type={previewSum > 100 ? "error" : previewSum > 90 ? "warning" : "info"}
                showIcon
                message={`Other active shares on this VG in overlapping window already sum to ${previewSum.toFixed(2)}%. Your share must keep the total ≤ 100%.`}
                style={{ marginTop: 8 }}
              />
            )}
          </Form>
        </Modal>
    </div>
  );
}
